/**
 * Google Analytics 4.
 *
 * Reproduce el fragmento oficial de Google, con dos diferencias deliberadas:
 * la etiqueta se carga desde el código en vez de estar escrita en el HTML,
 * lo que permite no cargarla en desarrollo, y el aviso de página se dispara a
 * mano en cada cambio de ruta —al ser una aplicación de una sola página, GA4
 * por su cuenta solo registraría la primera carga y toda la navegación
 * interna se perdería.
 */

/* Propiedad de Analytics: www.cabinadefotos.com.co */
const GA4_POR_DEFECTO = 'G-MCD58TZMHT';

/* La variable de entorno tiene prioridad, por si algún entorno necesita
   medir en otra propiedad. Si no está definida se usa la de arriba. */
const GA4_ID = (import.meta.env.VITE_GA4_ID as string | undefined) || GA4_POR_DEFECTO;

declare global {
  interface Window {
    dataLayer: unknown[];
    /* Opcional: hasta que se prepara la cola no existe. */
    gtag?: (...args: unknown[]) => void;
  }
}

/** No medir desde el entorno de desarrollo: ensucia las métricas reales. */
function esLocal(): boolean {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h.endsWith('.local');
}

/** Prepara la cola de comandos y devuelve la función para encolar. */
function prepararCola(): (...args: unknown[]) => void {
  window.dataLayer = window.dataLayer || [];

  if (!window.gtag) {
    /* Se empuja el objeto `arguments`, no un array. Es la forma exacta que
       documenta Google y la que su cola de comandos sabe interpretar; con un
       array los comandos pueden no procesarse y los datos se perderían en
       silencio, sin ningún error visible. */
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }

  return window.gtag;
}

/* ─── Conversiones ──────────────────────────────────────────────────
   Hasta octubre de 2026 GA4 no tenía ningún "evento clave": se sabía
   cuántas visitas había, pero no cuántas terminaban en un contacto. Estos
   eventos lo miden sin tocar la página: un solo listener en el documento.

     click_whatsapp   — cualquier enlace a wa.me / api.whatsapp.com
     click_telefono   — enlaces tel:
     click_email      — enlaces mailto:
     click_cotizar    — enlaces internos hacia /contacto (desde otra página)
     cotizador_inicio — primer clic dentro del survey de GHL en la página

   Cada evento lleva `ubicacion` (la ruta donde ocurrió) y `texto` (el
   texto del enlace), para saber QUÉ página y QUÉ botón convierten.
   En GA4 hay que marcarlos como "eventos clave" (Administrar → Eventos). */
function enviar(nombre: string, extra: Record<string, string> = {}): void {
  const gtag = prepararCola();
  gtag('event', nombre, { ubicacion: window.location.pathname, ...extra });
}

function escucharConversiones(): void {
  document.addEventListener(
    'click',
    (e) => {
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute('href') || '';
      const texto = (a.getAttribute('aria-label') || a.textContent || '').trim().slice(0, 80);
      if (/wa\.me|api\.whatsapp\.com/.test(href)) enviar('click_whatsapp', { texto });
      else if (href.startsWith('tel:')) enviar('click_telefono', { texto });
      else if (href.startsWith('mailto:')) enviar('click_email', { texto });
      else if (/^\/contacto(?:[#?]|$)/.test(href) && window.location.pathname !== '/contacto') {
        enviar('click_cotizar', { texto });
      }
    },
    { capture: true, passive: true }
  );

  /* El survey vive en un iframe de otro dominio: no se ven sus clics, pero
     cuando el visitante hace clic dentro, la ventana pierde el foco y el
     elemento activo pasa a ser ese iframe. Se registra una vez por página. */
  let ultimaRuta = '';
  window.addEventListener('blur', () => {
    const el = document.activeElement;
    if (el instanceof HTMLIFrameElement && /leadconnectorhq|msgsndr/.test(el.src) && ultimaRuta !== window.location.pathname) {
      ultimaRuta = window.location.pathname;
      enviar('cotizador_inicio');
    }
  });
}

export function initAnalytics(): void {
  if (!GA4_ID || typeof window === 'undefined' || esLocal()) return;
  if (document.getElementById('ga4')) return;

  const gtag = prepararCola();
  gtag('js', new Date());
  gtag('config', GA4_ID, { send_page_view: false });
  escucharConversiones();

  /* La primera visita la registra el efecto de ruta de App (trackPageView).
     Antes se registraba también aquí y cada entrada contaba doble. */

  /* La librería de Google pesa ~180 KB y ocupaba el procesador del celular
     justo mientras la página arrancaba. Se pide cuando la página ya cargó y
     el navegador está libre; mientras tanto los eventos esperan en la cola
     (dataLayer) y se envían todos al llegar, así que no se pierde nada. */
  const cargar = () => {
    if (document.getElementById('ga4')) return;
    const s = document.createElement('script');
    s.id = 'ga4';
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
  };
  const enReposo = () =>
    'requestIdleCallback' in window
      ? window.requestIdleCallback(cargar, { timeout: 4000 })
      : setTimeout(cargar, 1500);

  if (document.readyState === 'complete') enReposo();
  else window.addEventListener('load', enReposo, { once: true });
}

let primeraVista = true;

export function trackPageView(path: string): void {
  if (!GA4_ID || typeof window === 'undefined' || esLocal()) return;
  const gtag = prepararCola();
  const enviarVista = () =>
    gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });

  /* La primera vista llega con el HTML prerenderizado: el título ya es el
     correcto. En la navegación interna, en cambio, este efecto corre ANTES
     de que la página nueva (que se carga aparte) ponga su <title>, y GA4
     registraba la visita con el título de la página anterior. Se espera a
     que cambie el título, con un tope de 2 s por si la página no lo cambia. */
  if (primeraVista) {
    primeraVista = false;
    enviarVista();
    return;
  }
  const tituloAnterior = document.title;
  let enviada = false;
  const listo = () => {
    if (enviada) return;
    enviada = true;
    observador.disconnect();
    window.clearTimeout(tope);
    enviarVista();
  };
  const observador = new MutationObserver(() => {
    if (document.title !== tituloAnterior) listo();
  });
  observador.observe(document.head, { subtree: true, childList: true, characterData: true });
  const tope = window.setTimeout(listo, 2000);
}
